import { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import ReactQuill from "react-quill";

import DstButton from "../../../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../../../components/MultiSelectDropdown/MultiSelectDropdown";
import Popup from "../../../../../components/Popup/Popup";
import translate from "../../../../../services/Translate";
import { ReminderTemplatePopupProps } from "./ReminderTemplate.d";
import { getMeetingReminderTemplates } from "./ReminderTemplate.function";
import { sendMeetingReminder } from "./ReminderTemplate.function";

const MeetingReminderPopup = ({ show, onClose, meeting, language }: ReminderTemplatePopupProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
  const [templateContent, setTemplateContent] = useState("");
  const [reminderTemplates, setReminderTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [isSendingReminder, setIsSendingReminder] = useState(false);

  const _meetingUsers = useMemo(
    () =>
      meeting.attendees.map((user: any) => ({
        ...user,
        id: user.id,
        searchable_text: `${user.full_name} ${user.email}`,
      })),
    [meeting.attendees]
  );

  useEffect(() => {
    if (show) {
      getMeetingReminderTemplates(meeting.type.id, setReminderTemplates, setLoadingTemplates, language);
    }
  }, [show, meeting.type.id, language]);

  useEffect(() => {
    if (show) {
      setSelectedUsers(_meetingUsers);
    }
  }, [show, _meetingUsers]);

  const _handleClose = () => {
    setSelectedTemplate(null);
    setTemplateContent("");
    setSelectedUsers(_meetingUsers);
    setIsSendingReminder(false);
    onClose();
  };

  return (
    <Popup
      show={show}
      onClose={_handleClose}
      modalTitle={translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.TITLE")}
      size="xl"
      scrollable
    >
      <Form className="d-flex flex-column gap-3">
        <Form.Group>
          <Form.Label>
            {translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.SELECT_TEMPLATE")}
          </Form.Label>

          <MultiSelectDropdown
            items={reminderTemplates.map((template) => ({
              ...template,
              id: template.id,
              searchable_text: template.content,
            }))}
            isSingleSelect
            language={language}
            mock={loadingTemplates}
            displayItem={(item) => item.id}
            badgeItemProperty="id"
            label={
              selectedTemplate
                ? selectedTemplate.id
                : translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.SELECT_TEMPLATE")
            }
            onConfirm={(items) => {
              const template = items?.[0] || null;
              setSelectedTemplate(template);
              setTemplateContent(template?.content || "");
            }}
            toggleClass="w-100"
            borderToggle
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>{translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.SELECT_USERS")}</Form.Label>

          <MultiSelectDropdown
            items={_meetingUsers}
            defaultSelectedItems={_meetingUsers}
            language={language}
            displayItem={(item) => (
              <div className="d-flex flex-column">
                <span>{item.full_name}</span>
                <span className="text-muted xs-regular">{item.email}</span>
              </div>
            )}
            badgeItemProperty="full_name"
            label={
              selectedUsers.length > 0
                ? `${selectedUsers.length} ${translate(
                    language,
                    "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.USERS_SELECTED"
                  )}`
                : translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.SELECT_USERS")
            }
            onConfirm={setSelectedUsers}
            toggleClass="w-100"
            borderToggle
            showAlwaysConfirm
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>{translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.CONTENT")}</Form.Label>

          <ReactQuill
            value={templateContent}
            onChange={setTemplateContent}
            modules={{
              toolbar: [
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
            placeholder={translate(language, "PAGES.MEETING.MANAGE_MEETING.TEMPLATE_REMINDER.CONTENT_PLACEHOLDER")}
            preserveWhitespace
          />
        </Form.Group>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <DstButton variant="light" value={translate(language, "GLOBAL.CANCEL")} clickFunction={_handleClose} />

          <DstButton
            variant="primary"
            value={translate(language, "GLOBAL.CONFIRM")}
            disabled={!templateContent.trim() || selectedUsers.length === 0 || isSendingReminder}
            loading={isSendingReminder}
            clickFunction={() => {
              sendMeetingReminder(
                meeting.id,
                {
                  user_ids: selectedUsers.map((user) => user.id),
                  template_content: templateContent,
                },
                language,
                setIsSendingReminder,
                () => _handleClose()
              );
            }}
          />
        </div>
      </Form>
    </Popup>
  );
};

export default MeetingReminderPopup;
